import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { ShieldCheck, Plus, AlertTriangle, ExternalLink, Loader2, Edit2, Trash2 } from 'lucide-react';
import { Link } from 'react-router-dom';

const STATUS_STYLE = {
  active: 'bg-[#E8F5E9] text-[#1B5E20]',
  expiring: 'bg-[#FFF3E0] text-[#E65100]',
  expired: 'bg-[#FFEBEE] text-[#B71C1C]',
  unknown: 'bg-gray-100 text-gray-600',
};

export default function CredentialsPage() {
  const { api } = useAuth();
  const [creds, setCreds] = useState([]);
  const [types, setTypes] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, t, e] = await Promise.all([
        api.get('/credentials'),
        api.get('/credentials/types'),
        api.get('/employees'),
      ]);
      setCreds(c.data);
      setTypes(t.data);
      setEmployees(e.data.filter(u => u.is_active !== false && !['board_director', 'hr'].includes(u.role)));
    } catch (err) {
      toast.error('Failed to load credentials');
    } finally { setLoading(false); }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const del = async (c) => {
    if (!window.confirm(`Delete ${c.cert_label} for ${c.employee_name}?`)) return;
    try {
      await api.delete(`/credentials/${c.id}`);
      toast.success('Credential deleted');
      load();
    } catch (e) { toast.error('Delete failed'); }
  };

  const expired = creds.filter(c => c.status === 'expired');
  const expiring = creds.filter(c => c.status === 'expiring');

  // Group by employee for main table
  const byEmployee = {};
  creds.forEach(c => {
    if (!byEmployee[c.employee_id]) byEmployee[c.employee_id] = { name: c.employee_name, id: c.employee_id, list: [] };
    byEmployee[c.employee_id].list.push(c);
  });

  return (
    <div className="p-6 md:p-8 max-w-7xl mx-auto" data-testid="credentials-page">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-1">
        <div className="flex items-center gap-3">
          <ShieldCheck className="h-8 w-8 text-[#6B3FA0]" />
          <h1 className="text-4xl font-bold">Credentials</h1>
        </div>
        <Button onClick={() => { setEditing(null); setDialogOpen(true); }} data-testid="add-credential-btn">
          <Plus className="h-4 w-4 mr-1" /> Add credential
        </Button>
      </div>
      <p className="text-gray-600 mb-6">
        Staff clearances & certifications. Expired credentials block scheduling until renewed.
      </p>

      {/* ODP training summary card — links to Orientation & Training */}
      <Card className="mb-4 border-[#DAC58A] bg-[#FBF6E8]">
        <CardContent className="p-4 flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <ShieldCheck className="h-6 w-6 text-[#B08A2A]" />
            <div>
              <div className="font-semibold">ODP annual training hours</div>
              <div className="text-sm text-gray-600">Tracked automatically via Orientation & Training module transcripts.</div>
            </div>
          </div>
          <Link to="/onboarding" className="btn-primary-outline flex items-center gap-1.5 px-3 py-1.5 rounded border border-[#6B3FA0] text-[#6B3FA0] text-sm hover:bg-[#6B3FA0] hover:text-white transition-colors" data-testid="view-training-link">
            <ExternalLink className="h-3.5 w-3.5" /> View training portal
          </Link>
        </CardContent>
      </Card>

      {/* Alert banners */}
      {expired.length > 0 && (
        <Card className="mb-3 border-red-300 bg-red-50">
          <CardContent className="p-4 flex items-center gap-3" data-testid="expired-banner">
            <AlertTriangle className="h-5 w-5 text-red-600" />
            <div className="flex-1">
              <div className="font-semibold text-red-800">{expired.length} expired credential{expired.length > 1 ? 's' : ''} blocking scheduling</div>
              <div className="text-xs text-red-700">{expired.slice(0, 3).map(c => `${c.employee_name} — ${c.cert_label}`).join(' · ')}</div>
            </div>
          </CardContent>
        </Card>
      )}
      {expiring.length > 0 && (
        <Card className="mb-3 border-orange-300 bg-orange-50">
          <CardContent className="p-4 flex items-center gap-3" data-testid="expiring-banner">
            <AlertTriangle className="h-5 w-5 text-orange-600" />
            <div className="flex-1">
              <div className="font-semibold text-orange-800">{expiring.length} expiring within 30 days</div>
              <div className="text-xs text-orange-700">{expiring.slice(0, 3).map(c => `${c.employee_name} — ${c.cert_label} (${c.expiry_date})`).join(' · ')}</div>
            </div>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#6B3FA0]" /></div>
      ) : Object.keys(byEmployee).length === 0 ? (
        <Card><CardContent className="p-12 text-center text-gray-500">
          No credentials tracked yet. Click "Add credential" to get started.
        </CardContent></Card>
      ) : (
        <div className="space-y-3">
          {Object.values(byEmployee).sort((a, b) => a.name.localeCompare(b.name)).map(emp => (
            <Card key={emp.id}>
              <CardContent className="p-4">
                <div className="font-semibold text-lg mb-2">{emp.name}</div>
                <div className="grid gap-2">
                  {emp.list.map(c => (
                    <div key={c.id} className="flex items-center justify-between border rounded-lg p-3 flex-wrap gap-2" data-testid={`cred-${c.id}`}>
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <Badge className={`${STATUS_STYLE[c.status]} border-0 shrink-0`}>{c.status}</Badge>
                        <div>
                          <div className="font-medium">{c.cert_label}</div>
                          <div className="text-xs text-gray-500">
                            {c.issued_date && `Issued ${c.issued_date} · `}Expires {c.expiry_date}
                            {c.notes && ` · ${c.notes}`}
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <Button size="sm" variant="outline" onClick={() => { setEditing(c); setDialogOpen(true); }}
                                data-testid={`edit-cred-${c.id}`}>
                          <Edit2 className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => del(c)}
                                className="text-red-600 border-red-300 hover:bg-red-50"
                                data-testid={`delete-cred-${c.id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <CredentialDialog open={dialogOpen} onClose={() => setDialogOpen(false)}
                        api={api} types={types} employees={employees}
                        editing={editing} onSaved={load} />
    </div>
  );
}

function CredentialDialog({ open, onClose, api, types, employees, editing, onSaved }) {
  const [form, setForm] = useState({ employee_id: '', cert_type: '', custom_name: '', issued_date: '', expiry_date: '', notes: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editing) {
      setForm({
        employee_id: editing.employee_id || '',
        cert_type: editing.cert_type || '',
        custom_name: editing.custom_name || '',
        issued_date: editing.issued_date || '',
        expiry_date: editing.expiry_date || '',
        notes: editing.notes || '',
      });
    } else if (open) {
      setForm({ employee_id: '', cert_type: '', custom_name: '', issued_date: '', expiry_date: '', notes: '' });
    }
  }, [editing, open]);

  const save = async () => {
    if (!editing && (!form.employee_id || !form.cert_type)) {
      toast.error('Employee and credential type are required'); return;
    }
    if (!editing && form.cert_type === 'other' && !form.custom_name?.trim()) {
      toast.error('Custom credential name is required'); return;
    }
    if (!form.expiry_date) { toast.error('Expiry date is required'); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/credentials/${editing.id}`, {
          issued_date: form.issued_date, expiry_date: form.expiry_date, notes: form.notes,
          ...(editing.cert_type === 'other' ? { custom_name: form.custom_name } : {}),
        });
      } else {
        await api.post('/credentials', form);
      }
      toast.success(editing ? 'Credential updated' : 'Credential added');
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent data-testid="credential-dialog">
        <DialogHeader><DialogTitle>{editing ? 'Edit credential' : 'Add credential'}</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          {!editing && (
            <>
              <div>
                <Label>Staff member *</Label>
                <select value={form.employee_id} onChange={e => setField('employee_id', e.target.value)}
                        className="w-full border rounded px-3 py-2" data-testid="cred-employee">
                  <option value="">Choose staff…</option>
                  {employees.map(u => <option key={u._id || u.id} value={u._id || u.id}>{u.name}</option>)}
                </select>
              </div>
              <div>
                <Label>Credential type *</Label>
                <select value={form.cert_type} onChange={e => setField('cert_type', e.target.value)}
                        className="w-full border rounded px-3 py-2" data-testid="cred-type">
                  <option value="">Choose type…</option>
                  {types.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
                </select>
              </div>
              {form.cert_type === 'other' && (
                <div>
                  <Label>Custom credential name *</Label>
                  <Input value={form.custom_name || ''} onChange={e => setField('custom_name', e.target.value)}
                         placeholder="e.g., PBSF, CPI, DSP Certified"
                         maxLength={120} data-testid="cred-custom-name" />
                </div>
              )}
            </>
          )}
          {editing && editing.cert_type === 'other' && (
            <div>
              <Label>Custom credential name</Label>
              <Input value={form.custom_name || ''} onChange={e => setField('custom_name', e.target.value)}
                     maxLength={120} data-testid="cred-custom-name-edit" />
            </div>
          )}
          <div>
            <Label>Issued date</Label>
            <Input type="date" value={form.issued_date} onChange={e => setField('issued_date', e.target.value)}
                   data-testid="cred-issued" />
          </div>
          <div>
            <Label>Expiry date *</Label>
            <Input type="date" value={form.expiry_date} onChange={e => setField('expiry_date', e.target.value)}
                   required data-testid="cred-expiry" />
          </div>
          <div>
            <Label>Notes</Label>
            <Textarea value={form.notes} onChange={e => setField('notes', e.target.value)} rows={2}
                      placeholder="Certification body, cert #, etc." data-testid="cred-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="save-cred-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
